import { QueuedRequestCancellation } from '@/__generated__/graphql';
import { useModalStatusMessage } from '@/hooks';
import { useApolloClient, useMutation } from '@apollo/client';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useCallback } from 'react';
import { STOP_GATE_OPERATION } from '../graphql/mutations/stopGateOperation';
import { refreshActiveGateData } from '../utils/refreshActiveGateData';
import {
  classifyGateCommandOutcome,
  composeGateCommandMessage,
  GateCommandOutcome,
  severityForOutcome,
} from '../utils/gateCommandOutcome';

export const useStopGateOperation = () => {
  const { openModalSuccessMessage, openModalWarningMessage, openModalErrorMessage } =
    useModalStatusMessage();
  const { i18n } = useLingui();
  const [mutate, { loading }] = useMutation(STOP_GATE_OPERATION);
  const client = useApolloClient();

  const notify = useCallback(
    (
      outcome: GateCommandOutcome,
      payload: { code: string; message?: string | null },
      queuedRequestCancellation?: QueuedRequestCancellation | null,
    ) => {
      const queued =
        queuedRequestCancellation === QueuedRequestCancellation.Cancelled
          ? t(
              i18n,
            )`Se canceló la solicitud en cola. Si el equipo estaba recibiéndola en ese momento, la compuerta podría arrancar igual: verifique su estado.`
          : queuedRequestCancellation === QueuedRequestCancellation.Unknown
            ? t(i18n)`No se pudo confirmar si se canceló la solicitud en cola.`
            : null;
      const message = [composeGateCommandMessage(payload, outcome, i18n, 'stop'), queued]
        .filter(Boolean)
        .join(' ');
      // Con cualquier desenlace de la cola, el resultado del stop no alcanza para anunciar verde:
      // `UNKNOWN` puede dejar una solicitud viva, y `CANCELLED` no descarta que el despachador de
      // SGMO ya estuviera enviándola —el firmware contesta `GO04` y el `move` en vuelo arranca la
      // compuerta después; SGMO publica un stop propio, pero si falla Amaru no se entera (D-011).
      const severity =
        queuedRequestCancellation != null && severityForOutcome(outcome) === 'success'
          ? 'warning'
          : severityForOutcome(outcome);
      if (severity === 'success') openModalSuccessMessage(message);
      else if (severity === 'warning') openModalWarningMessage(message);
      else openModalErrorMessage(message);
    },
    [i18n, openModalSuccessMessage, openModalWarningMessage, openModalErrorMessage],
  );

  const stopOperation = useCallback(
    async (gateId: string) => {
      return mutate({
        variables: {
          gateId,
        },
        onCompleted({ stopGateOperation }) {
          const { queuedRequestCancellation } = stopGateOperation;
          notify(
            classifyGateCommandOutcome(stopGateOperation),
            stopGateOperation,
            queuedRequestCancellation,
          );
          // La cancelación de la cola no llega por la suscripción (no hay feed en vivo de la cola
          // hasta SGMO-25): una sola lectura, después de la acción del propio usuario, para que el
          // panel no siga en «En cola». No es polling.
          if (queuedRequestCancellation != null) void refreshActiveGateData(client);
        },
        onError(error) {
          // Sin respuesta no se puede afirmar que la compuerta sigue moviéndose ni que se detuvo.
          console.error(error.message);
          notify('unconfirmed', {
            code: 'REQUEST_FAILED',
            message: t(i18n)`No se recibió respuesta al comando de detención.`,
          });
        },
      });
    },
    [mutate, i18n, notify, client],
  );

  return {
    stopOperation,
    loading,
  };
};
